import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ujmsAddParty, ujmsScheduleHearing, ujmsSubmitFiling, ujmsAddDocketEntry,
  ujmsAssignCase, ujmsChangeStatus, ujmsCorrectDocket,
  ujmsOrderTemplates, ujmsDraftOrder, ujmsSubmitOrder,
  ujmsCreateOrderTemplate, ujmsDeleteOrderTemplate, ujmsRenderOrderTemplate,
} from "../../services/ujmsApi";
import { Field, inputCls, selectCls, btnPrimary, btnGhost, cls } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { RuleCiteButton, appendCitation } from "../RuleCite";
import { UploadDropzone } from "../enterprise/media/UploadDropzone";
import { humanSize } from "../../services/upload";

const wrap = (fn, onDone, onClose, msg) => async () => {
  try { await fn(); toast.success(msg); onDone(); onClose(); }
  catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
};

export function AddPartyDialog({ open, onClose, caseId, cfg, onDone }) {
  const [p, setP] = useState({ relationship_type: "Defendant", first_name: "", last_name: "", dob: "", org_name: "", attorney_name: "", party_type: "Person" });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-party-dialog">
        <DialogHeader><DialogTitle>Add Party</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Kind"><select className={selectCls} value={p.party_type} onChange={(e) => setP({ ...p, party_type: e.target.value })} data-testid="party-kind"><option>Person</option><option>Organization</option><option>Government Agency</option></select></Field>
          <Field label="Relationship"><select className={selectCls} value={p.relationship_type} onChange={(e) => setP({ ...p, relationship_type: e.target.value })} data-testid="party-relationship">{(cfg.relationship_types || []).map((r) => <option key={r.name}>{r.name}</option>)}</select></Field>
          {p.party_type === "Person" ? (<>
            <Field label="First Name"><input className={inputCls} value={p.first_name} onChange={(e) => setP({ ...p, first_name: e.target.value })} data-testid="party-first" /></Field>
            <Field label="Last Name"><input className={inputCls} value={p.last_name} onChange={(e) => setP({ ...p, last_name: e.target.value })} data-testid="party-last" /></Field>
            <Field label="DOB"><input type="date" className={inputCls} value={p.dob} onChange={(e) => setP({ ...p, dob: e.target.value })} /></Field>
          </>) : (
            <Field label="Organization Name"><input className={inputCls} value={p.org_name} onChange={(e) => setP({ ...p, org_name: e.target.value })} data-testid="party-org" /></Field>
          )}
          <Field label="Attorney"><input className={inputCls} value={p.attorney_name} onChange={(e) => setP({ ...p, attorney_name: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} data-testid="party-save-btn"
            onClick={wrap(() => ujmsAddParty(caseId, { ...p, dob: p.dob || undefined, attorney_name: p.attorney_name || undefined }), onDone, onClose, "PARTY_ADDED")}>Add Party</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Courtroom ⇄ judge defaults from Administration › Courtroom assignments. */
const roomOfJudge = (cfg, jid) => (cfg?.judges || []).find((j) => j.id === jid)?.default_courtroom_id || "";
const judgeOfRoom = (cfg, rid) => (cfg?.courtrooms || []).find((r) => r.id === rid)?.judicial_officer_id || "";

/** Manual date/time form — shared by the legacy dialog and the unified Schedule Hearing flow (Find session | Set manually). */
import { WeekAvailability } from '../enterprise/calendar/WeekAvailability';
export function ManualHearingForm({ caseId, cfg, c, onDone, onClose, testPrefix = "hearing", initialType }) {
  const [h, setH] = useState({ hearing_type: initialType || "FIRST_APPEARANCE", date: new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10), start_time: "09:30", duration_minutes: 30, judge_id: "", courtroom_id: "" });
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Hearing Type"><select className={selectCls} value={h.hearing_type} onChange={(e) => setH({ ...h, hearing_type: e.target.value })} data-testid={`${testPrefix}-type`}>{(cfg.hearing_types || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}</select></Field>
        <Field label="Date"><input type="date" className={inputCls} value={h.date} onChange={(e) => setH({ ...h, date: e.target.value })} data-testid={`${testPrefix}-date`} /></Field>
        <Field label="Start Time"><input type="time" className={inputCls} value={h.start_time} onChange={(e) => setH({ ...h, start_time: e.target.value })} data-testid={`${testPrefix}-time`} /></Field>
        <Field label="Duration (min)"><input type="number" className={inputCls} value={h.duration_minutes} onChange={(e) => setH({ ...h, duration_minutes: +e.target.value })} data-testid={`${testPrefix}-duration`}/></Field>
        <Field label="Judge"><select className={selectCls} value={h.judge_id || c.current_judge_id || ""} onChange={(e) => setH({ ...h, judge_id: e.target.value, courtroom_id: h.courtroom_id || roomOfJudge(cfg, e.target.value) })} data-testid={`${testPrefix}-judge`}><option value="">—</option>{(cfg.judges || []).map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}</select></Field>
        <Field label="Courtroom"><select className={selectCls} value={h.courtroom_id || c.current_courtroom_id || ""} onChange={(e) => setH({ ...h, courtroom_id: e.target.value, judge_id: h.judge_id || c.current_judge_id || judgeOfRoom(cfg, e.target.value) })} data-testid={`${testPrefix}-room`}><option value="">—</option>{(cfg.courtrooms || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
      </div>
      <WeekAvailability date={h.date} hearingType={h.hearing_type} judgeId={h.judge_id||c.current_judge_id} roomId={h.courtroom_id||c.current_courtroom_id} onSelect={date=>setH({...h,date})} testPrefix={testPrefix}/>
      <div className="flex justify-end gap-2 mt-3">
        <button className={btnGhost} onClick={onClose} data-testid={`${testPrefix}-cancel`}>Cancel</button>
        <button className={btnPrimary} data-testid={`${testPrefix}-save-btn`}
          onClick={wrap(() => ujmsScheduleHearing(caseId, { ...h, judge_id: h.judge_id || c.current_judge_id || undefined, courtroom_id: h.courtroom_id || c.current_courtroom_id || undefined }), onDone, onClose, "HEARING_SCHEDULED")}>Schedule</button>
      </div>
    </>
  );
}

export function ScheduleHearingDialog({ open, onClose, caseId, cfg, c, onDone }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="schedule-hearing-dialog">
        <DialogHeader><DialogTitle>Schedule Hearing</DialogTitle></DialogHeader>
        <ManualHearingForm caseId={caseId} cfg={cfg} c={c} onDone={onDone} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
}

export function AddFilingDialog({ open, onClose, caseId, cfg, onDone }) {
  const [f, setF] = useState({ filing_type: "Motion", filing_title: "", document: null });
  const onUploaded = (rec, file) => setF((cur) => ({ ...cur, filing_title: cur.filing_title || file.name.replace(/\.[^.]+$/, ""), document: { title: cur.filing_title || file.name.replace(/\.[^.]+$/, ""), filename: file.name, mime_type: rec.content_type || file.type || "application/pdf", upload_id: rec.upload_id, size: rec.size } }));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-filing-dialog">
        <DialogHeader><DialogTitle>Submit Filing</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Filing Type"><select className={selectCls} value={f.filing_type} onChange={(e) => setF({ ...f, filing_type: e.target.value })} data-testid="filing-type-select">{(cfg.filing_types || []).map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}</select></Field>
          <Field label="Title"><input className={inputCls} value={f.filing_title} onChange={(e) => setF({ ...f, filing_title: e.target.value })} data-testid="filing-title-input" /></Field>
          <Field label="Document"><UploadDropzone purpose="DOCUMENT" caseId={caseId} multiple={false} accept=".pdf,image/*,.doc,.docx,.txt" onUploaded={onUploaded} compact testId="filing-upload" hint="PDF, image or Word document · up to 100 MB · stored in court object storage" /></Field>
          {f.document && <p className="text-xs text-emerald-700" data-testid="filing-attached">Attached: {f.document.filename} ({humanSize(f.document.size)})</p>}
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!f.filing_title} data-testid="filing-save-btn"
            onClick={wrap(() => ujmsSubmitFiling({ case_id: caseId, filing_type: f.filing_type, filing_title: f.filing_title, document: f.document ? { ...f.document, title: f.filing_title } : null }), onDone, onClose, "FILING_RECEIVED — sent to clerk review")}>Submit</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One-click bench entries for judicial officers — each sets the event code + editable text. */
export const BENCH_DOCKET_PRESETS = [
  { label: "Continued", event_code: "CAUSE_CONTINUED", text: "Cause continued by order of Court to the next setting. Defendant's presence required. Bond/conditions to stand." },
  { label: "Under advisement", event_code: "TAKEN_UNDER_ADVISEMENT", text: "Matter heard and taken under advisement. Ruling to issue by written order." },
  { label: "Finding entered", event_code: "FINDING_ENTERED", text: "Finding entered on the record. Cause set for sentencing; pre-sentence investigation ordered." },
  { label: "Motion granted", event_code: "MOTION_GRANTED", text: "Motion heard and GRANTED for the reasons stated on the record." },
  { label: "Motion denied", event_code: "MOTION_DENIED", text: "Motion heard and DENIED for the reasons stated on the record." },
  { label: "Plea entered", event_code: "PLEA_ENTERED", text: "Defendant admonished pursuant to Supreme Court Rule 402; plea entered and accepted by the Court." },
  { label: "Bond continued", event_code: "BOND_CONTINUED", text: "Bond and all conditions of pretrial release to stand as previously set." },
];

export function DocketEntryDialog({ open, onClose, caseId, onDone, presets }) {
  const [d, setD] = useState({ event_code: presets ? "BENCH_ENTRY" : "CLERK_ENTRY", description: "" });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="docket-entry-dialog">
        <DialogHeader><DialogTitle>Manual Docket Entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {presets && (
            <div className="flex flex-wrap items-center gap-1" data-testid="docket-presets">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mr-1">Bench presets</span>
              {presets.map((p) => (
                <button key={p.event_code} type="button" className={cls(btnGhost, d.event_code === p.event_code && "!bg-ujms-dark !text-white !border-ujms-dark")} onClick={() => setD({ event_code: p.event_code, description: p.text })} data-testid={`docket-preset-${p.event_code.toLowerCase()}`}>{p.label}</button>
              ))}
            </div>
          )}
          <Field label="Event Code"><input className={inputCls} value={d.event_code} onChange={(e) => setD({ ...d, event_code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} data-testid="docket-code-input" /></Field>
          <Field label="Entry Text">
            <div className="space-y-1.5">
              <textarea className={inputCls} rows={3} value={d.description} onChange={(e) => setD({ ...d, description: e.target.value })} data-testid="docket-desc-input" />
              <RuleCiteButton testId="docket-cite-rule" onCite={(c) => setD((p) => ({ ...p, description: appendCitation(p.description, c) }))} />
            </div>
          </Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!d.description.trim()} data-testid="docket-save-btn"
            onClick={wrap(() => ujmsAddDocketEntry(caseId, d), onDone, onClose, "DOCKET_ENTRY_CREATED")}>Enter on Docket</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DraftOrderDialog({ open, onClose, caseId, onDone }) {
  const blank = { order_type: "GENERAL", title: "", body: "" };
  const [f, setF] = useState(blank);
  const [templates, setTemplates] = useState([]);
  const [tplQ, setTplQ] = useState("");
  const loadTemplates = () => ujmsOrderTemplates().then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    if (open) loadTemplates();
  }, [open, caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  const applyTemplate = async (t) => {
    try {
      const r = await ujmsRenderOrderTemplate(caseId, t.id);
      setF({ order_type: r.data.order_type, title: r.data.title, body: r.data.body });
      const n = (r.data.tokens_filled || []).length;
      toast.success(n ? `Template applied — ${n} field(s) auto-filled from the case record` : "Template applied");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply template"); }
  };
  const saveTemplate = async () => {
    try {
      await ujmsCreateOrderTemplate({ order_type: f.order_type, title: f.title, body: f.body });
      toast.success("Saved to the template library — reusable on any case");
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save template"); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.title}" from the library?`)) return;
    try {
      await ujmsDeleteOrderTemplate(t.id);
      toast.success("Template deleted");
      setTemplates((ts) => ts.filter((x) => x.id !== t.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete template"); }
  };
  const filteredTpl = templates.filter((t) => !tplQ.trim()
    || `${t.title} ${t.order_type} ${t.created_by || ""}`.toLowerCase().includes(tplQ.trim().toLowerCase()));
  const save = async (submit) => {
    try {
      const r = await ujmsDraftOrder(caseId, f);
      if (submit) {
        await ujmsSubmitOrder(r.data.id);
        toast.success("ORDER_SUBMITTED — awaiting judicial signature");
      } else toast.success("ORDER_DRAFTED");
      setF(blank); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="draft-order-dialog">
        <DialogHeader><DialogTitle>Draft Order</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">Start from a library template or write the order text; placeholders auto-fill from the case record. The order is rendered on the court's letterhead for signature.</div>
        {(
          <div className="space-y-3">
            <Field label="Template Library — click to start from a reusable order">
              <div className="space-y-1.5">
                <input className={inputCls} placeholder="Search templates…" value={tplQ}
                  onChange={(e) => setTplQ(e.target.value)} data-testid="tpl-search" />
                <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="tpl-list">
                  {filteredTpl.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-blue-50 cursor-pointer text-sm"
                      onClick={() => applyTemplate(t)} data-testid={`tpl-row-${t.id}`}>
                      <span className="flex-1 truncate">{t.title}</span>
                      {t.system_defined
                        ? <span className="text-[9px] font-mono font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-sm px-1 py-0.5">SYSTEM</span>
                        : <span className="text-[9px] font-mono font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded-sm px-1 py-0.5" title={`Created by ${t.created_by || "clerk"}`}>{(t.created_by || "CUSTOM").split(" ")[0].toUpperCase()}</span>}
                      {!t.system_defined && (
                        <button className="text-slate-400 hover:text-red-600 font-bold px-1" title="Delete template"
                          onClick={(e) => { e.stopPropagation(); deleteTemplate(t); }} data-testid={`tpl-del-${t.id}`}>×</button>)}
                    </div>
                  ))}
                  {!filteredTpl.length && <div className="px-2 py-2 text-xs text-slate-400" data-testid="tpl-empty">No templates match.</div>}
                </div>
                <div className="text-[11px] text-slate-400">
                  Placeholders auto-fill from this case when applied: {"{defendant} {plaintiff} {case_number} {caption} {judge} {division} {today} {next_hearing}"}
                </div>
              </div>
            </Field>
            <Field label="Title" required>
              <input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="order-title-input" />
            </Field>
            <Field label="Order Text" required>
              <div className="space-y-1.5">
                <textarea className={`${inputCls} font-serif`} rows={8} value={f.body}
                  onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="order-body-input" />
                <div className="flex items-center gap-2">
                  <RuleCiteButton testId="order-cite-rule" onCite={(c) => setF((p) => ({ ...p, body: appendCitation(p.body, c) }))} />
                  <button className="text-xs text-violet-700 underline decoration-dotted hover:text-violet-900"
                    disabled={!f.title.trim() || !f.body.trim()} onClick={saveTemplate}
                    title="Save the current title and text as a reusable library template"
                    data-testid="tpl-save-btn">Save as Template</button>
                </div>
              </div>
            </Field>
          </div>
        )}
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnGhost} disabled={!f.title.trim() || !f.body.trim()} onClick={() => save(false)} data-testid="order-save-draft-btn">Save Draft</button>
          <button className={btnPrimary} disabled={!f.title.trim() || !f.body.trim()} onClick={() => save(true)} data-testid="order-submit-btn">Draft &amp; Submit for Signature</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssignDialog({ open, onClose, caseId, cfg, c, onDone }) {
  const [a, setA] = useState({ judge_id: "", courtroom_id: "", calendar_id: "", reason: "" });
  const assigned = !!c.current_judge_id;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader><DialogTitle>{assigned ? "Reassign Case (override)" : "Assign Case"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <button className={btnGhost} data-testid="assign-auto-btn"
            onClick={wrap(() => ujmsAssignCase(caseId, { auto: true, override: assigned, reason: a.reason || "Rule-based reassignment" }), onDone, onClose, "CASE_ASSIGNED (rule-based)")}>
            Auto-assign by division rules
          </button>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Judge"><select className={selectCls} value={a.judge_id} onChange={(e) => setA({ ...a, judge_id: e.target.value, courtroom_id: roomOfJudge(cfg, e.target.value) || a.courtroom_id })} data-testid="assign-judge"><option value="">Select…</option>{(cfg.judges || []).map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}</select></Field>
            <Field label="Courtroom"><select className={selectCls} value={a.courtroom_id} onChange={(e) => setA({ ...a, courtroom_id: e.target.value, judge_id: a.judge_id || judgeOfRoom(cfg, e.target.value) })} data-testid="assign-room"><option value="">—</option>{(cfg.courtrooms || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
            <Field label="Calendar"><select className={selectCls} value={a.calendar_id} onChange={(e) => setA({ ...a, calendar_id: e.target.value })} data-testid="assign-calendar"><option value="">—</option>{(cfg.calendars || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
            <Field label={assigned ? "Override Reason (required)" : "Reason"}><input className={inputCls} value={a.reason} onChange={(e) => setA({ ...a, reason: e.target.value })} data-testid="assign-reason" /></Field>
          </div>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!a.judge_id || (assigned && !a.reason.trim())} data-testid="assign-save-btn"
            onClick={wrap(() => ujmsAssignCase(caseId, { ...a, override: assigned, judge_id: a.judge_id, courtroom_id: a.courtroom_id || undefined, calendar_id: a.calendar_id || undefined }), onDone, onClose, "CASE_ASSIGNED")}>Assign</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StatusDialog({ open, onClose, caseId, c, onDone }) {
  const [s, setS] = useState({ status: "ACTIVE", reason: "" });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="status-dialog">
        <DialogHeader><DialogTitle>Change Case Status (current: {c.status})</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New Status"><select className={selectCls} value={s.status} onChange={(e) => setS({ ...s, status: e.target.value })} data-testid="status-select">{["OPEN", "PENDING", "ACTIVE", "STAYED", "DISPOSED", "CLOSED", "REOPENED"].map((x) => <option key={x}>{x}</option>)}</select></Field>
          <Field label="Reason"><input className={inputCls} value={s.reason} onChange={(e) => setS({ ...s, reason: e.target.value })} data-testid="status-reason" /></Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} data-testid="status-save-btn"
            onClick={wrap(() => ujmsChangeStatus(caseId, s), onDone, onClose, "CASE_STATUS_CHANGED")}>Update Status</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CorrectDocketDialog({ entry, onClose, caseId, onDone }) {
  const [d, setD] = useState({ reason: "", corrected_text: "" });
  return (
    <Dialog open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="correct-docket-dialog">
        <DialogHeader><DialogTitle>Corrective Entry for Docket #{entry?.sequence_number}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 border border-slate-200 bg-slate-50 rounded-sm p-2">{entry?.description}</p>
        <div className="space-y-3">
          <Field label="Reason (required)"><input className={inputCls} value={d.reason} onChange={(e) => setD({ ...d, reason: e.target.value })} data-testid="correct-reason" /></Field>
          <Field label="Corrected Text (required)"><textarea className={inputCls} rows={3} value={d.corrected_text} onChange={(e) => setD({ ...d, corrected_text: e.target.value })} data-testid="correct-text" /></Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} disabled={!d.reason.trim() || !d.corrected_text.trim()} data-testid="correct-save-btn"
            onClick={wrap(() => ujmsCorrectDocket(caseId, entry.id, d), onDone, onClose, "DOCKET_ENTRY_CORRECTED — corrective entry filed")}>File Correction</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
